/**
 * WebsiteView.js — Portal Website tab (owner only)
 *
 * Control center for all restaurant website widgets and branding.
 * Reads from / writes to:
 *   - restaurants_data/{id}                 (General: allowShowMenuPrices; Socials: socials)
 *   - restaurants_data/{id}/website/config  (Design: brand colors + per-widget themes)
 *
 * Sub-tabs: General | Design | Socials
 */

import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import OnlineTab from './OnlineTab';

const _wvFunctions = getFunctions();
const listGBPLocationsFn  = httpsCallable(_wvFunctions, 'listGBPLocations');
const saveGBPLocationFn   = httpsCallable(_wvFunctions, 'saveGBPSelectedLocation');
const updateGBPProfileFn  = httpsCallable(_wvFunctions, 'updateGBPProfile');
const createGBPPostFn     = httpsCallable(_wvFunctions, 'createGBPPost');
const generateSchemaOrgFn = httpsCallable(_wvFunctions, 'generateSchemaOrg');

// ── Shared UI ─────────────────────────────────────────────────────────────────

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <p className="text-sm font-semibold text-neutral-800">{title}</p>
    {description && <p className="text-sm text-neutral-400 mt-0.5">{description}</p>}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-neutral-200 rounded-xl p-5 ${className}`}>
    {children}
  </div>
);

// ── Color helpers ─────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const clean = (hex || '#000000').replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16) || 0,
    g: parseInt(clean.slice(2, 4), 16) || 0,
    b: parseInt(clean.slice(4, 6), 16) || 0,
  };
}

function valueToHex(value) {
  if (!value) return '#000000';
  if (value.startsWith('#')) return value.slice(0, 7);
  const m = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return '#000000';
  return '#' + [m[1], m[2], m[3]]
    .map(n => parseInt(n, 10).toString(16).padStart(2, '0'))
    .join('');
}

function valueToOpacityPct(value) {
  if (!value) return 100;
  const m = value.match(/rgba\([^,]+,[^,]+,[^,]+,\s*([\d.]+)\)/);
  return m ? Math.round(parseFloat(m[1]) * 100) : 100;
}

function buildRgba(hex, opacityPct) {
  const { r, g, b } = hexToRgb(hex);
  const a = (Math.min(100, Math.max(0, opacityPct)) / 100).toFixed(2);
  return `rgba(${r},${g},${b},${a})`;
}

// ── ColorPickerRow ────────────────────────────────────────────────────────────
// Renders a label + native color swatch + optional opacity input + brand quick-picks.
// When hasOpacity=true, the stored value is rgba(); otherwise it's a hex string.

const ColorPickerRow = ({ label, description, value, onChange, brandColors = [], hasOpacity = false }) => {
  const isEmpty = !value;
  const hex = valueToHex(value);
  const opacityPct = hasOpacity ? valueToOpacityPct(value) : 100;

  const applyHex = (newHex) => {
    if (hasOpacity) {
      onChange(buildRgba(newHex, opacityPct));
    } else {
      onChange(newHex);
    }
  };

  const applyOpacity = (e) => {
    const pct = parseInt(e.target.value, 10);
    onChange(buildRgba(hex, isNaN(pct) ? 100 : pct));
  };

  const applyBrandSwatch = (brandHex) => {
    if (!brandHex || !brandHex.startsWith('#')) return;
    if (hasOpacity) {
      onChange(buildRgba(brandHex, opacityPct));
    } else {
      onChange(brandHex);
    }
  };

  const validBrandColors = brandColors.filter(c => c.value && c.value.startsWith('#'));

  return (
    <div className="py-3 border-b border-neutral-100 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-neutral-700">{label}</p>
          {description && <p className="text-xs text-neutral-400 mt-0.5">{description}</p>}
        </div>
        {!isEmpty && (
          <button onClick={() => onChange('')} className="text-xs text-neutral-400 hover:text-red-400 shrink-0 ml-2">
            Clear
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Color swatch — clicking opens native OS color picker */}
        <div className="relative shrink-0 w-9 h-9 rounded-lg border border-neutral-200 overflow-hidden"
          style={{ backgroundColor: isEmpty ? '#e5e7eb' : hex }}>
          <input
            type="color"
            value={hex}
            onChange={e => applyHex(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* Opacity (only when hasOpacity and a color is set) */}
        {hasOpacity && !isEmpty && (
          <div className="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min={0}
              max={100}
              value={opacityPct}
              onChange={applyOpacity}
              className="w-14 text-sm border border-neutral-200 rounded-lg px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-neutral-900"
            />
            <span className="text-xs text-neutral-400">%</span>
          </div>
        )}

        {/* Brand color quick-picks */}
        {validBrandColors.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-neutral-400 shrink-0">Quick:</span>
            {validBrandColors.map(c => (
              <button
                key={c.label}
                title={c.label}
                onClick={() => applyBrandSwatch(c.value)}
                className="w-6 h-6 rounded border border-neutral-200 shrink-0 hover:scale-110 transition-transform"
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
        )}

        {/* Value preview */}
        {isEmpty
          ? <span className="text-xs text-neutral-300 italic">Not set — widget default</span>
          : <span className="text-xs text-neutral-400 font-mono truncate max-w-[140px]">{value}</span>
        }
      </div>
    </div>
  );
};

// ── OverviewTab ───────────────────────────────────────────────────────────────

const OverviewTab = ({ restaurantId }) => {
  const [allowShowMenuPrices, setAllowShowMenuPrices] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        if (snap.exists()) {
          const d = snap.data();
          setAllowShowMenuPrices(!!d.allowShowMenuPrices);
        }
      } catch (e) {
        console.error('[WebsiteView/Overview] load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId]);

  const toggle = async (value) => {
    setSaving(true);
    setAllowShowMenuPrices(value);
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), { allowShowMenuPrices: value });
    } catch (e) {
      console.error('[WebsiteView/Overview] save failed:', e);
      setAllowShowMenuPrices(v => !v);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 py-6 text-center">Loading…</p>;

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <SectionHeader title="Menu" />
        <div className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-700">Show Menu Prices on Website</p>
            <p className="text-xs text-neutral-400 mt-0.5">Display item prices on the public-facing web menu.</p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button
              onClick={() => toggle(true)}
              disabled={saving}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                allowShowMenuPrices ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
              }`}
            >Yes</button>
            <button
              onClick={() => toggle(false)}
              disabled={saving}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                !allowShowMenuPrices ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
              }`}
            >No</button>
          </div>
        </div>
      </Card>

      <OnlineTab restaurantId={restaurantId} />
    </div>
  );
};

// ── DesignTab ─────────────────────────────────────────────────────────────────

const DESIGN_DEFAULTS = {
  primaryColor: '',
  secondaryColor: '',
  textOnPrimary: '',
  dropdownBgGradientStart: '',
  dropdownBgGradientEnd: '',
  dropdownBgSolid: '',
  dropdownBorderColor: '',
  dropdownTextColor: '',
  dropdownFontStyle: '',
  dropdownWidth: 0,
  widgetTableAvailableColor: '',
};

const DesignTab = ({ restaurantId }) => {
  const [config, setConfig] = useState(DESIGN_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId, 'website', 'config'));
        if (snap.exists()) setConfig(prev => ({ ...prev, ...snap.data() }));
      } catch (e) {
        console.error('[WebsiteView/Design] load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId]);

  const set = (field) => (value) => setConfig(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await setDoc(
        doc(db, 'restaurants_data', restaurantId, 'website', 'config'),
        { ...config, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('[WebsiteView/Design] save failed:', e);
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Brand colors as quick-pick swatches for widget color fields
  const brandColors = [
    { label: 'Primary',         value: config.primaryColor   },
    { label: 'Secondary',       value: config.secondaryColor },
    { label: 'Text on Primary', value: config.textOnPrimary  },
  ];

  if (loading) return <p className="text-sm text-neutral-400 py-6 text-center">Loading…</p>;

  return (
    <div className="space-y-4 max-w-2xl">

      {/* ── Brand Colors ──────────────────────────────────────────────────── */}
      <Card>
        <SectionHeader
          title="Brand Colors"
          description="Your restaurant's core palette. Once set, these appear as quick-pick swatches in every widget color field below — keeping all widgets visually consistent without matching colors by hand."
        />
        <ColorPickerRow
          label="Primary Color"
          description="Main brand color — buttons, accents, gradients."
          value={config.primaryColor}
          onChange={set('primaryColor')}
          brandColors={[]}
        />
        <ColorPickerRow
          label="Secondary Color"
          description="Supporting accent color."
          value={config.secondaryColor}
          onChange={set('secondaryColor')}
          brandColors={[]}
        />
        <ColorPickerRow
          label="Text on Primary"
          description="Readable text color when placed on a primary-colored background."
          value={config.textOnPrimary}
          onChange={set('textOnPrimary')}
          brandColors={brandColors}
        />
      </Card>

      {/* ── Nav Dropdown ──────────────────────────────────────────────────── */}
      <Card>
        <SectionHeader
          title="Nav Dropdown"
          description="Styles the navigation panel that opens from the menu button on the restaurant website. Leave any field blank to use the widget's built-in default."
        />

        <ColorPickerRow
          label="Background — Top"
          description="Top of the panel gradient. Opacity slider lets you make it semi-transparent."
          value={config.dropdownBgGradientStart}
          onChange={set('dropdownBgGradientStart')}
          brandColors={brandColors}
          hasOpacity
        />
        <ColorPickerRow
          label="Background — Bottom"
          description="Bottom of the panel gradient."
          value={config.dropdownBgGradientEnd}
          onChange={set('dropdownBgGradientEnd')}
          brandColors={brandColors}
          hasOpacity
        />
        <ColorPickerRow
          label="Solid Background"
          description="Used only when no gradient is set above."
          value={config.dropdownBgSolid}
          onChange={set('dropdownBgSolid')}
          brandColors={brandColors}
          hasOpacity
        />
        <ColorPickerRow
          label="Border Color"
          value={config.dropdownBorderColor}
          onChange={set('dropdownBorderColor')}
          brandColors={brandColors}
        />
        <ColorPickerRow
          label="Text Color"
          value={config.dropdownTextColor}
          onChange={set('dropdownTextColor')}
          brandColors={brandColors}
        />

        {/* Font style */}
        <div className="py-3 border-b border-neutral-100">
          <p className="text-sm font-medium text-neutral-700 mb-2">Font Style</p>
          <div className="flex items-center gap-2 flex-wrap">
            {['normal', 'italic'].map(style => (
              <button
                key={style}
                onClick={() => set('dropdownFontStyle')(config.dropdownFontStyle === style ? '' : style)}
                style={{ fontStyle: style }}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors ${
                  config.dropdownFontStyle === style
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                }`}
              >
                {style}
              </button>
            ))}
            {config.dropdownFontStyle && (
              <button onClick={() => set('dropdownFontStyle')('')} className="text-xs text-neutral-400 hover:text-red-400 px-1">
                Clear
              </button>
            )}
            {!config.dropdownFontStyle && (
              <span className="text-xs text-neutral-300 italic">Not set — widget default</span>
            )}
          </div>
        </div>

        {/* Panel width */}
        <div className="py-3">
          <p className="text-sm font-medium text-neutral-700 mb-2">Panel Width</p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={200}
              max={600}
              value={config.dropdownWidth || ''}
              onChange={e => set('dropdownWidth')(parseInt(e.target.value, 10) || 0)}
              placeholder="310"
              className="w-24 text-sm border border-neutral-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-neutral-900"
            />
            <span className="text-xs text-neutral-400">px — leave blank for default (310)</span>
          </div>
        </div>
      </Card>

      {/* ── Table Appearance ──────────────────────────────────────────────── */}
      <Card>
        <SectionHeader
          title="Reservation Widget — Table Appearance"
          description="Controls the color of available tables on the floor map in the reservation booking widget. Leave blank to use the default yellow glow."
        />
        <ColorPickerRow
          label="Available Table Color"
          description="Glow and badge color for tables that can be booked. Unavailable/reserved tables always show red."
          value={config.widgetTableAvailableColor}
          onChange={set('widgetTableAvailableColor')}
          brandColors={brandColors}
        />
      </Card>

      {/* Save */}
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex items-center gap-3 pb-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Design'}
        </button>
        {saved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
      </div>

    </div>
  );
};

// ── SocialsTab ────────────────────────────────────────────────────────────────

const SOCIAL_PLATFORMS = [
  { key: 'instagram',   label: 'Instagram',   placeholder: 'https://instagram.com/yourhandle',  icon: '📸' },
  { key: 'facebook',    label: 'Facebook',    placeholder: 'https://facebook.com/yourpage',      icon: '👥' },
  { key: 'tiktok',      label: 'TikTok',      placeholder: 'https://tiktok.com/@yourhandle',     icon: '🎵' },
  { key: 'twitter',     label: 'X / Twitter', placeholder: 'https://x.com/yourhandle',           icon: '𝕏'  },
  { key: 'youtube',     label: 'YouTube',     placeholder: 'https://youtube.com/yourchannel',    icon: '▶️' },
  { key: 'yelp',        label: 'Yelp',        placeholder: 'https://yelp.com/biz/your-listing',  icon: '⭐' },
  { key: 'tripadvisor', label: 'TripAdvisor', placeholder: 'https://tripadvisor.com/...',        icon: '🦉' },
  { key: 'opentable',   label: 'OpenTable',   placeholder: 'https://opentable.com/...',          icon: '🍽️' },
  { key: 'doordash',    label: 'DoorDash',    placeholder: 'https://doordash.com/...',           icon: '🛵' },
  { key: 'ubereats',    label: 'Uber Eats',   placeholder: 'https://ubereats.com/...',           icon: '🟢' },
  { key: 'grubhub',     label: 'Grubhub',     placeholder: 'https://grubhub.com/...',            icon: '🟠' },
];

const EMPTY_SOCIALS = Object.fromEntries(SOCIAL_PLATFORMS.map(p => [p.key, '']));

const SocialsTab = ({ restaurantId }) => {
  const [socials, setSocials] = useState(EMPTY_SOCIALS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        if (snap.exists()) setSocials({ ...EMPTY_SOCIALS, ...(snap.data().socials || {}) });
      } catch (e) {
        console.error('[WebsiteView/Socials] load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const cleaned = Object.fromEntries(
        Object.entries(socials).map(([k, v]) => [k, v.trim()])
      );
      await updateDoc(doc(db, 'restaurants_data', restaurantId), { socials: cleaned });
      setSocials(cleaned);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('[WebsiteView/Socials] save failed:', e);
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 py-6 text-center">Loading…</p>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-neutral-800">Social Media &amp; Directory Links</h3>
          <p className="text-xs text-neutral-400 mt-0.5">
            Paste the full URL for each platform. Leave blank to hide it on the restaurant website.
            Links appear in the site header, nav menu, and footer.
          </p>
        </div>

        <div className="space-y-3">
          {SOCIAL_PLATFORMS.map(({ key, label, placeholder, icon }) => (
            <div key={key} className="flex items-center gap-3">
              <span className="text-lg w-7 shrink-0 text-center select-none" title={label}>{icon}</span>
              <label className="w-28 shrink-0 text-sm font-medium text-neutral-700">{label}</label>
              <input
                type="url"
                value={socials[key] || ''}
                onChange={e => setSocials(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={placeholder}
                className="flex-1 text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-900 placeholder:text-neutral-300"
              />
              {socials[key] && (
                <a
                  href={socials[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-blue hover:underline shrink-0"
                >
                  Preview ↗
                </a>
              )}
            </div>
          ))}
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-xl hover:bg-neutral-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Links'}
          </button>
          {saved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
        </div>
      </div>
    </div>
  );
};

// ── Google Business Profile Tab ───────────────────────────────────────────────

const GBP_ATTR_GROUPS = [
  {
    label: 'Dining Options',
    attrs: [
      { key: 'hasDineIn',         label: 'Dine-in'          },
      { key: 'hasTakeout',        label: 'Takeout'           },
      { key: 'hasDelivery',       label: 'Delivery'          },
      { key: 'hasOutdoorSeating', label: 'Outdoor Seating'   },
      { key: 'takesReservations', label: 'Accepts Reservations' },
    ],
  },
  {
    label: 'Meals Served',
    attrs: [
      { key: 'servesBreakfast',   label: 'Breakfast'   },
      { key: 'servesLunch',       label: 'Lunch'       },
      { key: 'servesDinner',      label: 'Dinner'      },
      { key: 'servesBrunch',      label: 'Brunch'      },
      { key: 'servesHappyHour',   label: 'Happy Hour'  },
    ],
  },
  {
    label: 'Drinks',
    attrs: [
      { key: 'servesBeer',        label: 'Beer'        },
      { key: 'servesWine',        label: 'Wine'        },
      { key: 'servesCocktails',   label: 'Cocktails'   },
    ],
  },
  {
    label: 'Amenities',
    attrs: [
      { key: 'hasWifi',           label: 'Free Wi-Fi'              },
      { key: 'hasParking',        label: 'Parking Available'       },
      { key: 'hasLiveMusic',      label: 'Live Music'              },
      { key: 'dogsAllowed',       label: 'Dogs Allowed'            },
      { key: 'goodForGroups',     label: 'Good for Groups'         },
      { key: 'goodForKids',       label: 'Good for Kids'           },
      { key: 'goodForWatchingSports', label: 'Good for Watching Sports' },
    ],
  },
  {
    label: 'Accessibility',
    attrs: [
      { key: 'wheelchairAccessible', label: 'Wheelchair Accessible Entrance' },
    ],
  },
];

const PRICE_LEVELS = [
  { value: 'PRICE_LEVEL_INEXPENSIVE',  label: '$',    tip: 'Under $15' },
  { value: 'PRICE_LEVEL_MODERATE',     label: '$$',   tip: '$15–$30'   },
  { value: 'PRICE_LEVEL_EXPENSIVE',    label: '$$$',  tip: '$31–$60'   },
  { value: 'PRICE_LEVEL_VERY_EXPENSIVE', label: '$$$$', tip: 'Over $60' },
];

const GBPTab = ({ restaurantId }) => {
  const [loading, setLoading]         = useState(true);
  const [gbpAuth, setGbpAuth]         = useState(null);
  const [gbpCreds, setGbpCreds]       = useState({ clientId: '', clientSecret: '' });
  const [credSaving, setCredSaving]   = useState(false);

  const [locations, setLocations]     = useState([]);
  const [locLoading, setLocLoading]   = useState(false);
  const [locError, setLocError]       = useState('');

  const [description, setDescription] = useState('');
  const [websiteUri, setWebsiteUri]   = useState('');
  const [phone, setPhone]             = useState('');
  const [priceLevel, setPriceLevel]   = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState('');

  const [attrs, setAttrs]             = useState({});
  const [attrSaving, setAttrSaving]   = useState(false);
  const [attrStatus, setAttrStatus]   = useState('');

  const [featuredItems, setFeaturedItems]   = useState([]);
  const [postingItemId, setPostingItemId]   = useState(null);
  const [postStatus, setPostStatus]         = useState({});
  const [customPost, setCustomPost]         = useState('');
  const [customCta, setCustomCta]           = useState('');
  const [customCtaUrl, setCustomCtaUrl]     = useState('');
  const [customPosting, setCustomPosting]   = useState(false);
  const [customPostStatus, setCustomPostStatus] = useState('');

  const [schemaSettings, setSchemaSettings] = useState({
    cuisineTypes: '',
    facebook: '', instagram: '', yelp: '', tripadvisor: '',
    includeMenuItems: true,
    includeHours: true,
    websiteUrl: '',
  });
  const [schemaGenerating, setSchemaGenerating] = useState(false);
  const [schemaOutput, setSchemaOutput]         = useState('');
  const [schemaStatus, setSchemaStatus]         = useState('');

  useEffect(() => {
    if (!restaurantId) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        const data = snap.data() || {};
        setGbpAuth(data.gbpAuth || null);
        setGbpCreds({
          clientId:     data.gbpOAuthCredentials?.clientId     || '',
          clientSecret: data.gbpOAuthCredentials?.clientSecret || '',
        });
        const p = data.gbpProfile || {};
        setDescription(p.description  || '');
        setWebsiteUri( p.websiteUri   || '');
        setPhone(      p.phone        || '');
        setPriceLevel( p.priceLevel   || '');
        setAttrs(data.gbpAttributes || {});
        const s = data.schemaOrg || {};
        setSchemaSettings(prev => ({
          ...prev,
          cuisineTypes:     (s.cuisineTypes || []).join(', '),
          includeMenuItems: s.includeMenuItems !== false,
          includeHours:     s.includeHours     !== false,
          websiteUrl:       s.websiteUrl        || '',
          facebook:    s.socialUrls?.facebook    || '',
          instagram:   s.socialUrls?.instagram   || '',
          yelp:        s.socialUrls?.yelp        || '',
          tripadvisor: s.socialUrls?.tripadvisor || '',
        }));
      } catch (e) {
        console.warn('[GBPTab] load error:', e);
      } finally {
        setLoading(false);
      }
    };
    const loadFeatured = async () => {
      try {
        const qSnap = await getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'items'),
          where('isFeatured', '==', true)
        ));
        setFeaturedItems(qSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn('[GBPTab] featured items load error:', e);
      }
    };
    load();
    loadFeatured();
  }, [restaurantId]);

  const isLinked    = !!gbpAuth?.refreshToken;
  const hasLocation = !!gbpAuth?.locationId;

  const handleSaveCreds = async () => {
    if (!gbpCreds.clientId.trim() || !gbpCreds.clientSecret.trim()) return;
    setCredSaving(true);
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        'gbpOAuthCredentials.clientId':     gbpCreds.clientId.trim(),
        'gbpOAuthCredentials.clientSecret': gbpCreds.clientSecret.trim(),
      });
    } catch (e) {
      console.warn('[GBPTab] cred save error:', e);
    } finally {
      setCredSaving(false);
    }
  };

  const handleConnect = () => {
    window.open(
      `https://us-central1-gohost-b65d2.cloudfunctions.net/startGBPAuth?restaurantId=${restaurantId}`,
      '_blank',
      'width=600,height=700'
    );
  };

  const handleRefreshStatus = async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
      setGbpAuth(snap.data()?.gbpAuth || null);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchLocations = async () => {
    setLocLoading(true);
    setLocError('');
    try {
      const result = await listGBPLocationsFn({ restaurantId });
      setLocations(result.data.locations || []);
      if ((result.data.locations || []).length === 0) {
        setLocError('No locations found on this Google account.');
      }
    } catch (e) {
      setLocError(e.message || 'Failed to load locations.');
    } finally {
      setLocLoading(false);
    }
  };

  const handleSelectLocation = async (loc) => {
    setLocLoading(true);
    try {
      await saveGBPLocationFn({ restaurantId, accountId: loc.accountId, locationId: loc.locationId });
      const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
      setGbpAuth(snap.data()?.gbpAuth || null);
      setLocations([]);
    } catch (e) {
      setLocError(e.message || 'Failed to save location.');
    } finally {
      setLocLoading(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('Unlink Google Business Profile? Hours and profile will no longer sync to Google.')) return;
    await updateDoc(doc(db, 'restaurants_data', restaurantId), { gbpAuth: null });
    setGbpAuth(null);
    setLocations([]);
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileStatus('');
    try {
      await updateGBPProfileFn({
        restaurantId,
        description: description.slice(0, 750),
        websiteUri,
        phone,
        priceLevel: priceLevel || undefined,
      });
      setProfileStatus('✓ Saved & pushed to Google');
    } catch (e) {
      setProfileStatus(`Error: ${e.message}`);
    } finally {
      setProfileSaving(false);
      setTimeout(() => setProfileStatus(''), 4000);
    }
  };

  const handleSaveAttrs = async () => {
    setAttrSaving(true);
    setAttrStatus('');
    try {
      await updateGBPProfileFn({ restaurantId, boolAttributes: attrs });
      setAttrStatus('✓ Saved & pushed to Google');
    } catch (e) {
      setAttrStatus(`Error: ${e.message}`);
    } finally {
      setAttrSaving(false);
      setTimeout(() => setAttrStatus(''), 4000);
    }
  };

  const handlePostItem = async (item) => {
    setPostingItemId(item.id);
    try {
      const summary = [
        item.name,
        item.description ? `— ${item.description}` : '',
        item.price ? `$${item.price}` : '',
      ].filter(Boolean).join(' ');
      await createGBPPostFn({
        restaurantId,
        summary: summary.slice(0, 1500),
        mediaUrl: item.imageUrl || undefined,
      });
      setPostStatus(prev => ({ ...prev, [item.id]: 'ok' }));
      setTimeout(() => setPostStatus(prev => { const n = { ...prev }; delete n[item.id]; return n; }), 5000);
    } catch (e) {
      setPostStatus(prev => ({ ...prev, [item.id]: 'err' }));
      setTimeout(() => setPostStatus(prev => { const n = { ...prev }; delete n[item.id]; return n; }), 5000);
    } finally {
      setPostingItemId(null);
    }
  };

  const handleCustomPost = async () => {
    if (!customPost.trim()) return;
    setCustomPosting(true);
    setCustomPostStatus('');
    try {
      await createGBPPostFn({
        restaurantId,
        summary: customPost.trim(),
        callToActionType: customCta || undefined,
        callToActionUrl:  customCtaUrl.trim() || undefined,
      });
      setCustomPostStatus('✓ Posted to Google Business Profile');
      setCustomPost('');
      setCustomCta('');
      setCustomCtaUrl('');
    } catch (e) {
      setCustomPostStatus(`Error: ${e.message}`);
    } finally {
      setCustomPosting(false);
      setTimeout(() => setCustomPostStatus(''), 5000);
    }
  };

  const handleGenerateSchema = async () => {
    setSchemaGenerating(true);
    setSchemaStatus('');
    try {
      const cuisineTypes = schemaSettings.cuisineTypes
        .split(',').map(s => s.trim()).filter(Boolean);
      const socialUrls = {
        facebook:    schemaSettings.facebook.trim()    || undefined,
        instagram:   schemaSettings.instagram.trim()   || undefined,
        yelp:        schemaSettings.yelp.trim()        || undefined,
        tripadvisor: schemaSettings.tripadvisor.trim() || undefined,
      };
      const result = await generateSchemaOrgFn({
        restaurantId,
        cuisineTypes,
        socialUrls,
        includeMenuItems: schemaSettings.includeMenuItems,
        includeHours:     schemaSettings.includeHours,
        websiteUrl:       schemaSettings.websiteUrl.trim() || undefined,
      });
      setSchemaOutput(result.data.jsonLd);
      setSchemaStatus('✓ Generated');
    } catch (e) {
      setSchemaStatus(`Error: ${e.message}`);
    } finally {
      setSchemaGenerating(false);
      setTimeout(() => setSchemaStatus(''), 4000);
    }
  };

  if (loading) return <div className="text-sm text-neutral-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4">

      {/* ── Connection Card ── */}
      <Card>
        <SectionHeader
          title="Google Business Profile"
          description="Connect your GBP to sync hours, profile info, and attributes directly from GoHost."
        />
        <div className="flex items-center gap-3 mb-5">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
            hasLocation ? 'bg-emerald-50 text-emerald-700' :
            isLinked    ? 'bg-amber-50 text-amber-700' :
                          'bg-neutral-100 text-neutral-500'
          }`}>
            <span>{hasLocation ? '●' : isLinked ? '◐' : '○'}</span>
            {hasLocation ? 'Connected' : isLinked ? 'Linked — select location' : 'Not connected'}
          </span>
          {isLinked && gbpAuth?.locationId && (
            <span className="text-xs text-neutral-400 truncate">{gbpAuth.locationId}</span>
          )}
        </div>
        <div className="space-y-3 mb-4">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Google OAuth Credentials</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Client ID</label>
              <input
                type="text"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                placeholder="Google OAuth Client ID"
                value={gbpCreds.clientId}
                onChange={e => setGbpCreds(p => ({ ...p, clientId: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-neutral-500 mb-1 block">Client Secret</label>
              <input
                type="password"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                placeholder="Google OAuth Client Secret"
                value={gbpCreds.clientSecret}
                onChange={e => setGbpCreds(p => ({ ...p, clientSecret: e.target.value }))}
              />
            </div>
          </div>
          <button
            onClick={handleSaveCreds}
            disabled={credSaving || !gbpCreds.clientId || !gbpCreds.clientSecret}
            className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg disabled:opacity-40"
          >
            {credSaving ? 'Saving…' : 'Save Credentials'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 pt-3 border-t border-neutral-100">
          {!isLinked ? (
            <button
              onClick={handleConnect}
              disabled={!gbpCreds.clientId || !gbpCreds.clientSecret}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              Connect Google Business Profile ↗
            </button>
          ) : (
            <>
              <button
                onClick={handleRefreshStatus}
                className="px-3 py-2 text-sm font-medium border border-neutral-200 rounded-lg hover:bg-neutral-50"
              >
                Refresh Status
              </button>
              <button
                onClick={handleUnlink}
                className="px-3 py-2 text-sm font-medium text-red-500 border border-red-100 rounded-lg hover:bg-red-50"
              >
                Unlink
              </button>
            </>
          )}
          {!hasLocation && isLinked && (
            <button
              onClick={handleFetchLocations}
              disabled={locLoading}
              className="px-4 py-2 text-sm font-semibold bg-brand-gold text-white rounded-lg disabled:opacity-40"
            >
              {locLoading ? 'Loading locations…' : 'Select Location →'}
            </button>
          )}
        </div>
        {locations.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Choose a location</p>
            {locations.map(loc => (
              <button
                key={loc.locationId}
                onClick={() => handleSelectLocation(loc)}
                className="w-full text-left px-4 py-3 border border-neutral-200 rounded-xl hover:border-brand-blue hover:bg-blue-50 transition-colors"
              >
                <p className="text-sm font-semibold text-neutral-800">{loc.title || loc.locationId}</p>
                {loc.primaryCategory && (
                  <p className="text-xs text-neutral-400 mt-0.5">{loc.primaryCategory}</p>
                )}
                {loc.address?.addressLines && (
                  <p className="text-xs text-neutral-400">{loc.address.addressLines.join(', ')}</p>
                )}
              </button>
            ))}
          </div>
        )}
        {locError && <p className="mt-2 text-xs text-red-500">{locError}</p>}
        {isLinked && !hasLocation && (
          <p className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
            GBP is linked but no location is selected yet. Click "Select Location" to finish setup.
          </p>
        )}
      </Card>

      {hasLocation && (
        <Card>
          <SectionHeader
            title="Profile & SEO Fields"
            description="These fields appear on your Google listing and directly impact local search ranking."
          />
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                Business Description
                <span className="ml-1 font-normal normal-case text-neutral-400">({description.length}/750)</span>
              </label>
              <textarea
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-brand-blue"
                rows={4}
                maxLength={750}
                placeholder="Describe your restaurant — cuisine, atmosphere, what makes you unique."
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">Website URL</label>
                <input
                  type="url"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  placeholder="https://yourrestaurant.com"
                  value={websiteUri}
                  onChange={e => setWebsiteUri(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">Primary Phone</label>
                <input
                  type="tel"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  placeholder="+1 555-555-5555"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-2">Price Range</label>
              <div className="flex gap-2 flex-wrap">
                {PRICE_LEVELS.map(pl => (
                  <button
                    key={pl.value}
                    onClick={() => setPriceLevel(pl.value)}
                    title={pl.tip}
                    className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                      priceLevel === pl.value
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    {pl.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveProfile}
                disabled={profileSaving}
                className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
              >
                {profileSaving ? 'Pushing to Google…' : 'Save & Push to Google'}
              </button>
              {profileStatus && (
                <span className={`text-sm font-medium ${profileStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                  {profileStatus}
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      {hasLocation && (
        <Card>
          <SectionHeader
            title="Search Attributes"
            description="Attributes appear as search filters on Google Maps and improve discoverability."
          />
          <div className="space-y-5">
            {GBP_ATTR_GROUPS.map(group => (
              <div key={group.label}>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">{group.label}</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {group.attrs.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 cursor-pointer">
                      <div className="relative inline-flex">
                        <input type="checkbox" className="sr-only" checked={!!attrs[key]} onChange={e => setAttrs(prev => ({ ...prev, [key]: e.target.checked }))} />
                        <div
                          onClick={() => setAttrs(prev => ({ ...prev, [key]: !prev[key] }))}
                          className={`w-10 h-6 rounded-full transition-colors cursor-pointer ${attrs[key] ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${attrs[key] ? 'translate-x-5' : 'translate-x-1'}`} />
                        </div>
                      </div>
                      <span className="text-sm text-neutral-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
              <button
                onClick={handleSaveAttrs}
                disabled={attrSaving}
                className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
              >
                {attrSaving ? 'Pushing to Google…' : 'Save & Push to Google'}
              </button>
              {attrStatus && (
                <span className={`text-sm font-medium ${attrStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                  {attrStatus}
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      {hasLocation && (
        <Card>
          <SectionHeader
            title="GBP Posts — Featured Items"
            description={`Post menu items or announcements directly to your Google listing's "Updates" section.`}
          />
          <div className="space-y-5">
            {featuredItems.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Featured Items ({featuredItems.length})</p>
                <p className="text-xs text-neutral-400 -mt-1">Mark items as Featured in the Menus tab to show them here.</p>
                {featuredItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between gap-3 px-4 py-3 border border-neutral-200 rounded-xl">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-800 truncate">{item.name}</p>
                      {item.description && <p className="text-xs text-neutral-400 truncate">{item.description}</p>}
                      {item.price != null && <p className="text-xs text-neutral-500 mt-0.5">${item.price}</p>}
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      {postStatus[item.id] === 'ok' && <span className="text-xs text-emerald-600 font-medium">✓ Posted</span>}
                      {postStatus[item.id] === 'err' && <span className="text-xs text-red-500 font-medium">Failed</span>}
                      <button
                        onClick={() => handlePostItem(item)}
                        disabled={postingItemId === item.id}
                        className="px-3 py-1.5 text-xs font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
                      >
                        {postingItemId === item.id ? 'Posting…' : 'Post to GBP'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-400 italic">No featured items yet. Toggle "Featured (GBP / SEO)" on any item in the Menus tab.</p>
            )}
            <div className="border-t border-neutral-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Custom Post</p>
              <textarea
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-brand-blue"
                rows={3}
                maxLength={1500}
                placeholder="Write a custom announcement, promotion, or update for your Google listing…"
                value={customPost}
                onChange={e => setCustomPost(e.target.value)}
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-neutral-500 mb-1 block">Call to Action (optional)</label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    value={customCta}
                    onChange={e => setCustomCta(e.target.value)}
                  >
                    <option value="">None</option>
                    <option value="ORDER_ONLINE">Order Online</option>
                    <option value="BOOK">Book Now</option>
                    <option value="LEARN_MORE">Learn More</option>
                    <option value="SIGN_UP">Sign Up</option>
                    <option value="VISIT">Visit</option>
                    <option value="GET_OFFER">Get Offer</option>
                    <option value="CALL">Call Now</option>
                  </select>
                </div>
                {customCta && (
                  <div>
                    <label className="text-xs text-neutral-500 mb-1 block">CTA URL</label>
                    <input
                      type="url"
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="https://…"
                      value={customCtaUrl}
                      onChange={e => setCustomCtaUrl(e.target.value)}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCustomPost}
                  disabled={customPosting || !customPost.trim()}
                  className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
                >
                  {customPosting ? 'Posting…' : 'Post to GBP'}
                </button>
                {customPostStatus && (
                  <span className={`text-sm font-medium ${customPostStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                    {customPostStatus}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <SectionHeader
          title="Schema.org Structured Data"
          description="Generates a Restaurant JSON-LD block for your website. Google reads this for Knowledge Panel enrichment, rich results, and local SEO signals."
        />
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">Cuisine Types</label>
            <input
              type="text"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="Italian, Pizza, Pasta  (comma-separated)"
              value={schemaSettings.cuisineTypes}
              onChange={e => setSchemaSettings(p => ({ ...p, cuisineTypes: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
              Website URL <span className="font-normal normal-case text-neutral-400">(overrides GBP website)</span>
            </label>
            <input
              type="url"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="https://yourrestaurant.com"
              value={schemaSettings.websiteUrl}
              onChange={e => setSchemaSettings(p => ({ ...p, websiteUrl: e.target.value }))}
            />
          </div>
          <div>
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Social Profiles (sameAs)</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { key: 'facebook', label: 'Facebook' },
                { key: 'instagram', label: 'Instagram' },
                { key: 'yelp', label: 'Yelp' },
                { key: 'tripadvisor', label: 'TripAdvisor' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-xs text-neutral-500 mb-1 block">{label}</label>
                  <input
                    type="url"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder={`https://${key}.com/yourpage`}
                    value={schemaSettings[key]}
                    onChange={e => setSchemaSettings(p => ({ ...p, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-neutral-300 text-brand-blue"
                checked={schemaSettings.includeMenuItems}
                onChange={e => setSchemaSettings(p => ({ ...p, includeMenuItems: e.target.checked }))}
              />
              <span className="text-sm text-neutral-700">Include featured menu items</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-neutral-300 text-brand-blue"
                checked={schemaSettings.includeHours}
                onChange={e => setSchemaSettings(p => ({ ...p, includeHours: e.target.checked }))}
              />
              <span className="text-sm text-neutral-700">Include opening hours</span>
            </label>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleGenerateSchema}
              disabled={schemaGenerating}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {schemaGenerating ? 'Generating…' : 'Generate JSON-LD'}
            </button>
            {schemaStatus && (
              <span className={`text-sm font-medium ${schemaStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {schemaStatus}
              </span>
            )}
          </div>
          {schemaOutput && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Generated Output</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(`<script type="application/ld+json">\n${schemaOutput}\n</script>`); }}
                  className="text-xs px-3 py-1.5 border border-neutral-200 rounded-lg text-neutral-600 hover:border-neutral-400"
                >
                  Copy &lt;script&gt; tag
                </button>
              </div>
              <pre className="bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-xs text-neutral-700 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
                {schemaOutput}
              </pre>
              <p className="text-xs text-neutral-400">
                Paste this inside the <code>&lt;head&gt;</code> of your restaurant website. Re-generate whenever your hours, menu, or profile changes.
              </p>
            </div>
          )}
        </div>
      </Card>

    </div>
  );
};

// ── WebsiteView ───────────────────────────────────────────────────────────────

const WebsiteView = ({ restaurantId }) => {
  const [subTab, setSubTab] = useState('overview');

  const TAB_LABELS = { overview: 'Overview', design: 'Design', socials: 'Socials', google: 'Google' };

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto">
        {['overview', 'design', 'socials', 'google'].map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className={`shrink-0 px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
              subTab === t
                ? 'border-neutral-900 text-neutral-900'
                : 'border-transparent text-neutral-400 hover:text-neutral-600'
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {subTab === 'overview' && <OverviewTab restaurantId={restaurantId} />}
      {subTab === 'design'   && <DesignTab   restaurantId={restaurantId} />}
      {subTab === 'socials'  && <SocialsTab  restaurantId={restaurantId} />}
      {subTab === 'google'   && <GBPTab      restaurantId={restaurantId} />}
    </div>
  );
};

export default WebsiteView;
